import PropTypes from "prop-types";
import React, { Component } from "react";
import { Grid, Box, CircularProgress, Icon } from "@material-ui/core";
import { withRouter, Link as ReactRouterLink } from "react-router-dom";
import {
  Details as DetailsIcon,
  Description as DescriptionIcon,
  ErrorOutline as ErrorOutlineIcon,
  DateRange as DateRangeIcon,
  BarChart as ChartIcon,
  InsertChart as InsertChartIcon,
  Note as NoteIcon,
  Mic as MicIcon
} from "@material-ui/icons";
import styled from "styled-components";
import { withTranslation } from "react-i18next";
import HeaderProfile from "../../waybee-ui/HeaderProfile";
import MenuButton from "../../waybee-ui/MenuButton";

import StudentService from "../../services/StudentService";
import MenuService from "../../services/MenuService";
import BackButton from "../../components/BackButton";
import NewFeatureBadge from "../../components/NewFeatureBadge";
import { Can, AbilityContext } from "../../acl/can";
import StudentClassStatus from "./components/StudentClassStatus";
import { buildMenuTarget, DIARY_STUDENT_MENU } from "../../utils/dynamicMenu";

const MenuTileWrap = styled.div`
  position: relative;
`;

const Link = props => (
  <ReactRouterLink {...props} style={{ textDecoration: "none" }} />
);

const customMenuIcon = iconName => <Icon>{iconName || "list_alt"}</Icon>;

class StudentProfile extends Component {
  constructor(props) {
    super(props);
    this.state = {
      student: null,
      customMenus: [],
      dynamicMenus: []
    };
  }

  componentDidMount() {
    this.getStudentProfile();
  }

  getStudentProfile = async () => {
    const { match } = this.props;
    const { studentId } = match.params;
    const [student, customMenus, dynamicMenus] = await Promise.all([
      StudentService.getStudentById(studentId),
      StudentService.getCustomMenus(studentId).catch(() => []),
      MenuService.list(DIARY_STUDENT_MENU).catch(() => [])
    ]);
    this.setState({
      student,
      customMenus: customMenus || [],
      dynamicMenus: dynamicMenus || []
    });
  };

  render() {
    const { student, customMenus, dynamicMenus } = this.state;
    const { location, t } = this.props;
    const studentClass = localStorage.getItem("class");
    const fromStudents =
      location && location.state && location.state.from === "/students";
    let backTo = "/classes";
    if (fromStudents) {
      backTo = "/students";
    } else if (studentClass) {
      backTo = `/classes/${studentClass}`;
    }

    if (!student) {
      return (
        <Box p={6} textAlign="center">
          <CircularProgress />
        </Box>
      );
    }

    return (
      <Grid container spacing={2}>
        <BackButton to={backTo} />

        <Grid item xs={12}>
          <HeaderProfile
            name={student.name}
            info={t("student.raInfo", { ra: student.ra })}
            photo={student.photo}
          />
        </Grid>
        <Grid item xs={12}>
          <StudentClassStatus
            classStatus={student.classStatus}
            studentId={student.ra || student.id}
          />
        </Grid>
        <Grid item xs={12}>
          <Can I="use module" a="studentContactInfo">
            <Link to={`${student.id}/info`}>
              <MenuButton icon={<DetailsIcon />}>
                {t("student.menu.generalInfo")}
              </MenuButton>
            </Link>
          </Can>
          <Can I="use module" a="diaryStudentRecord">
            <Link to={`${student.id}/notes`}>
              <MenuButton icon={<NoteIcon />}>
                {t("student.menu.notes")}
              </MenuButton>
            </Link>
          </Can>
          <Can I="use module" a="diaryStudentBulletin">
            <Link to={`${student.id}/statistics`}>
              <MenuButton icon={<DescriptionIcon />}>
                {t("student.menu.bulletin")}
              </MenuButton>
            </Link>
          </Can>
          <Can I="use module" a="diaryStudentGraphics">
            <Link to={`${student.id}/charts`}>
              <MenuButton icon={<ChartIcon />}>
                {t("student.menu.charts")}
              </MenuButton>
            </Link>
          </Can>
          <Can I="use module" a="diaryStudentOccurrences">
            <Link to={`${student.id}/occurrences`}>
              <AbilityContext.Consumer>
                {ability => (
                  <MenuButton icon={<ErrorOutlineIcon />}>
                    {ability &&
                    ability.can("use module", "diaryOccurrenceAdvanced")
                      ? t("student.menu.occurrencesAdvanced")
                      : t("student.menu.occurrences")}
                  </MenuButton>
                )}
              </AbilityContext.Consumer>
            </Link>
          </Can>
          <Can I="use module" a="diaryStudentSchedule">
            <Link to={`${student.id}/schedule`}>
              <MenuButton icon={<DateRangeIcon />}>
                {t("student.menu.schedule")}
              </MenuButton>
            </Link>
          </Can>
          <Can I="use module" a="reports">
            <Link to={`${student.id}/reports`}>
              <MenuButton icon={<InsertChartIcon />}>
                {t("student.menu.reports")}
              </MenuButton>
            </Link>
          </Can>
          <Can I="use module" a="smartMinutes">
            <MenuTileWrap>
              <NewFeatureBadge floating top="10px" right="12px" />
              <Link to={`/student/${student.ra || student.id}/ata-inteligente`}>
                <MenuButton icon={<MicIcon />}>
                  {t("student.menu.smartMinutes")}
                </MenuButton>
              </Link>
            </MenuTileWrap>
          </Can>
          {(dynamicMenus || []).map(menu => (
            <Link
              key={menu.id}
              to={buildMenuTarget(menu, { studentId: student.id })}
            >
              <MenuButton icon={customMenuIcon(menu.icon)}>
                {menu.title}
              </MenuButton>
            </Link>
          ))}
          {(customMenus || []).map(menu => (
            <Link key={menu.key} to={`${student.id}/custom/${menu.key}`}>
              <MenuButton icon={customMenuIcon(menu.icon)}>
                {menu.label}
              </MenuButton>
            </Link>
          ))}
        </Grid>
      </Grid>
    );
  }
}

StudentProfile.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.objectOf(PropTypes.string)
  }).isRequired,
  history: PropTypes.shape({
    goBack: PropTypes.func
  }).isRequired,
  location: PropTypes.shape({
    state: PropTypes.shape({
      from: PropTypes.string
    })
  }),
  t: PropTypes.func.isRequired
};

StudentProfile.defaultProps = {
  location: null
};

export default withRouter(withTranslation()(StudentProfile));
